import type { FunctionComponent } from "react";

import { BaseLink } from "~/components/common/base-link";
import { OffsetContainer } from "~/components/posties/offset-container";
import type { BillingCycle, Bundle, Pricing } from "~/components/posties/subscription-widget/types";

import IconBlueBalloon from "./blue-balloon.svg";
import IconMascotPeter from "./mascot-peter.png";
import IconMascotPippa from "./mascot-pippa.png";
import IconRedBalloon from "./red-balloon.svg";
import {
  BalloonContainer,
  BillingCycleLabel,
  Content,
  Currency,
  Description,
  Icon,
  IntroPrice,
  Mascot,
  PriceContainer,
  PriceDescription,
  RackPrice,
  Title,
} from "./styles";

type Props = {
  billingCycle: BillingCycle;
  className?: string;
  handleSelected?: () => void;
  isSelected?: boolean;
  pricing: Pricing;
  variant: Bundle;
  withBalloon: boolean;
  withLink?: {
    url: string;
  };
};

export const BundleOption: FunctionComponent<Props> = ({
  billingCycle,
  className,
  handleSelected,
  isSelected,
  pricing,
  variant,
  withBalloon,
  withLink,
}) => {
  const computeViewModel = (variant: Bundle, cycle: BillingCycle) => {
    const price = cycle === "monthly" ? pricing.monthly : pricing.annual;
    const computePriceDescription = () => {
      const totalAnnualPrice = (price.intro ?? price.rack) * 12;
      return `Total HKD ${totalAnnualPrice} for the first year`;
    };
    const priceDescription = computePriceDescription();
    const backgroundColor = isSelected ? "#f8ffac" : "";

    switch (variant) {
      case "epaper-bundle":
        return {
          backgroundColor,
          balloonOption: {
            icon: <IconBlueBalloon />,
            position: "top-left" as const,
          },
          description: "Enjoy our digital platform and e-version of the printed copy",
          iconBackground: "#0f75bf",
          mascot: IconMascotPippa,
          price,
          priceDescription,
          title: "ePaper + Digital",
        };
      case "print-bundle":
        return {
          backgroundColor,
          balloonOption: {
            icon: <IconRedBalloon />,
            position: "top-right" as const,
          },
          description:
            "Enjoy our digital platform with physical printed copy delivered to you, free of charge",
          iconBackground: "#ff9cb3",
          mascot: IconMascotPeter,
          price,
          priceDescription,
          title: "Print + Digital",
        };
    }
  };

  const viewModel = computeViewModel(variant, billingCycle);

  const renderContent = () => (
    <Content onClick={() => handleSelected?.()}>
      <Icon backgroundColor={viewModel.iconBackground}>
        <Mascot src={viewModel.mascot.src} />
        <BalloonContainer $isVisible={withBalloon} $position={viewModel.balloonOption.position}>
          {viewModel.balloonOption.icon}
        </BalloonContainer>
      </Icon>
      <Title>{viewModel.title}</Title>
      <PriceContainer>
        <Currency>HKD</Currency>
        {viewModel.price.intro && <IntroPrice>{viewModel.price.intro}</IntroPrice>}
        <RackPrice>{viewModel.price.rack}</RackPrice>
        <BillingCycleLabel>/month</BillingCycleLabel>
      </PriceContainer>
      <PriceDescription>{viewModel.priceDescription}</PriceDescription>
      <Description>{viewModel.description}</Description>
    </Content>
  );

  return (
    <OffsetContainer
      backgroundColor={viewModel.backgroundColor}
      borderRadius="20px"
      className={className}
      offset={6}
    >
      {withLink ? (
        <BaseLink pathname={withLink.url} target="_blank">
          {renderContent()}
        </BaseLink>
      ) : (
        renderContent()
      )}
    </OffsetContainer>
  );
};

BundleOption.displayName = "BundleOption";
